import { Dispatch } from 'redux';
import {
  getNotificationPreferencesApi,
  updateNotificationPreferencesApi,
  updateQuietHoursApi,
  updateDigestPreferencesApi,
  getChannelSubscriptionsApi,
  subscribeToChannelApi,
  unsubscribeFromChannelApi,
  muteChannelApi,
  unmuteChannelApi,
  getPostSubscriptionsApi,
  subscribeToPostApi,
  unsubscribeFromPostApi,
  mutePostApi,
  unmutePostApi,
  getCommunityPolicyApi,
  updateCommunityPolicyApi,
  getChannelPolicyApi,
  updateChannelPolicyApi,
  INotificationPreference,
  IUserChannelSubscription,
  IUserPostSubscription,
  ICommunityNotificationPolicy,
  IChannelNotificationPolicy,
} from '../../api/notificationSettings';

// ========== Action Types ==========

// User Preferences
export const GET_NOTIFICATION_PREFERENCES_LOADING = 'GET_NOTIFICATION_PREFERENCES_LOADING';
export const GET_NOTIFICATION_PREFERENCES_SUCCESS = 'GET_NOTIFICATION_PREFERENCES_SUCCESS';
export const GET_NOTIFICATION_PREFERENCES_ERROR = 'GET_NOTIFICATION_PREFERENCES_ERROR';

export const UPDATE_NOTIFICATION_PREFERENCES_LOADING = 'UPDATE_NOTIFICATION_PREFERENCES_LOADING';
export const UPDATE_NOTIFICATION_PREFERENCES_SUCCESS = 'UPDATE_NOTIFICATION_PREFERENCES_SUCCESS';
export const UPDATE_NOTIFICATION_PREFERENCES_ERROR = 'UPDATE_NOTIFICATION_PREFERENCES_ERROR';

export const UPDATE_QUIET_HOURS_LOADING = 'UPDATE_QUIET_HOURS_LOADING';
export const UPDATE_QUIET_HOURS_SUCCESS = 'UPDATE_QUIET_HOURS_SUCCESS';
export const UPDATE_QUIET_HOURS_ERROR = 'UPDATE_QUIET_HOURS_ERROR';

export const UPDATE_DIGEST_PREFERENCES_LOADING = 'UPDATE_DIGEST_PREFERENCES_LOADING';
export const UPDATE_DIGEST_PREFERENCES_SUCCESS = 'UPDATE_DIGEST_PREFERENCES_SUCCESS';
export const UPDATE_DIGEST_PREFERENCES_ERROR = 'UPDATE_DIGEST_PREFERENCES_ERROR';

// Channel Subscriptions
export const GET_CHANNEL_SUBSCRIPTIONS_LOADING = 'GET_CHANNEL_SUBSCRIPTIONS_LOADING';
export const GET_CHANNEL_SUBSCRIPTIONS_SUCCESS = 'GET_CHANNEL_SUBSCRIPTIONS_SUCCESS';
export const GET_CHANNEL_SUBSCRIPTIONS_ERROR = 'GET_CHANNEL_SUBSCRIPTIONS_ERROR';

export const SUBSCRIBE_TO_CHANNEL_LOADING = 'SUBSCRIBE_TO_CHANNEL_LOADING';
export const SUBSCRIBE_TO_CHANNEL_SUCCESS = 'SUBSCRIBE_TO_CHANNEL_SUCCESS';
export const SUBSCRIBE_TO_CHANNEL_ERROR = 'SUBSCRIBE_TO_CHANNEL_ERROR';

export const UNSUBSCRIBE_FROM_CHANNEL_LOADING = 'UNSUBSCRIBE_FROM_CHANNEL_LOADING';
export const UNSUBSCRIBE_FROM_CHANNEL_SUCCESS = 'UNSUBSCRIBE_FROM_CHANNEL_SUCCESS';
export const UNSUBSCRIBE_FROM_CHANNEL_ERROR = 'UNSUBSCRIBE_FROM_CHANNEL_ERROR';

export const MUTE_CHANNEL_LOADING = 'MUTE_CHANNEL_LOADING';
export const MUTE_CHANNEL_SUCCESS = 'MUTE_CHANNEL_SUCCESS';
export const MUTE_CHANNEL_ERROR = 'MUTE_CHANNEL_ERROR';

export const UNMUTE_CHANNEL_LOADING = 'UNMUTE_CHANNEL_LOADING';
export const UNMUTE_CHANNEL_SUCCESS = 'UNMUTE_CHANNEL_SUCCESS';
export const UNMUTE_CHANNEL_ERROR = 'UNMUTE_CHANNEL_ERROR';

// Post Subscriptions
export const GET_POST_SUBSCRIPTIONS_LOADING = 'GET_POST_SUBSCRIPTIONS_LOADING';
export const GET_POST_SUBSCRIPTIONS_SUCCESS = 'GET_POST_SUBSCRIPTIONS_SUCCESS';
export const GET_POST_SUBSCRIPTIONS_ERROR = 'GET_POST_SUBSCRIPTIONS_ERROR';

export const SUBSCRIBE_TO_POST_LOADING = 'SUBSCRIBE_TO_POST_LOADING';
export const SUBSCRIBE_TO_POST_SUCCESS = 'SUBSCRIBE_TO_POST_SUCCESS';
export const SUBSCRIBE_TO_POST_ERROR = 'SUBSCRIBE_TO_POST_ERROR';

export const UNSUBSCRIBE_FROM_POST_LOADING = 'UNSUBSCRIBE_FROM_POST_LOADING';
export const UNSUBSCRIBE_FROM_POST_SUCCESS = 'UNSUBSCRIBE_FROM_POST_SUCCESS';
export const UNSUBSCRIBE_FROM_POST_ERROR = 'UNSUBSCRIBE_FROM_POST_ERROR';

export const MUTE_POST_LOADING = 'MUTE_POST_LOADING';
export const MUTE_POST_SUCCESS = 'MUTE_POST_SUCCESS';
export const MUTE_POST_ERROR = 'MUTE_POST_ERROR';

export const UNMUTE_POST_LOADING = 'UNMUTE_POST_LOADING';
export const UNMUTE_POST_SUCCESS = 'UNMUTE_POST_SUCCESS';
export const UNMUTE_POST_ERROR = 'UNMUTE_POST_ERROR';

// Admin Policies
export const GET_COMMUNITY_POLICY_LOADING = 'GET_COMMUNITY_POLICY_LOADING';
export const GET_COMMUNITY_POLICY_SUCCESS = 'GET_COMMUNITY_POLICY_SUCCESS';
export const GET_COMMUNITY_POLICY_ERROR = 'GET_COMMUNITY_POLICY_ERROR';

export const UPDATE_COMMUNITY_POLICY_LOADING = 'UPDATE_COMMUNITY_POLICY_LOADING';
export const UPDATE_COMMUNITY_POLICY_SUCCESS = 'UPDATE_COMMUNITY_POLICY_SUCCESS';
export const UPDATE_COMMUNITY_POLICY_ERROR = 'UPDATE_COMMUNITY_POLICY_ERROR';

export const GET_CHANNEL_POLICY_LOADING = 'GET_CHANNEL_POLICY_LOADING';
export const GET_CHANNEL_POLICY_SUCCESS = 'GET_CHANNEL_POLICY_SUCCESS';
export const GET_CHANNEL_POLICY_ERROR = 'GET_CHANNEL_POLICY_ERROR';

export const UPDATE_CHANNEL_POLICY_LOADING = 'UPDATE_CHANNEL_POLICY_LOADING';
export const UPDATE_CHANNEL_POLICY_SUCCESS = 'UPDATE_CHANNEL_POLICY_SUCCESS';
export const UPDATE_CHANNEL_POLICY_ERROR = 'UPDATE_CHANNEL_POLICY_ERROR';

// ========== Action Creators ==========

// User Preferences

export const fetchNotificationPreferences = () => async (dispatch: Dispatch) => {
  dispatch({ type: GET_NOTIFICATION_PREFERENCES_LOADING });
  try {
    const response = await getNotificationPreferencesApi();
    dispatch({
      type: GET_NOTIFICATION_PREFERENCES_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: GET_NOTIFICATION_PREFERENCES_ERROR,
      payload: error.response?.data?.message || 'Failed to fetch notification preferences',
    });
    throw error;
  }
};

export const updateNotificationPreferences = (data: {
  emailEnabled?: boolean;
  pushEnabled?: boolean;
  globalFrequency?: 'immediate' | 'daily' | 'off';
}) => async (dispatch: Dispatch) => {
  dispatch({ type: UPDATE_NOTIFICATION_PREFERENCES_LOADING });
  try {
    const response = await updateNotificationPreferencesApi(data);
    dispatch({
      type: UPDATE_NOTIFICATION_PREFERENCES_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: UPDATE_NOTIFICATION_PREFERENCES_ERROR,
      payload: error.response?.data?.message || 'Failed to update notification preferences',
    });
    throw error;
  }
};

export const updateQuietHours = (data: {
  start: string;
  end: string;
  timezone: string;
}) => async (dispatch: Dispatch) => {
  dispatch({ type: UPDATE_QUIET_HOURS_LOADING });
  try {
    const response = await updateQuietHoursApi(data);
    dispatch({
      type: UPDATE_QUIET_HOURS_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: UPDATE_QUIET_HOURS_ERROR,
      payload: error.response?.data?.message || 'Failed to update quiet hours',
    });
    throw error;
  }
};

export const updateDigestPreferences = (data: {
  community?: 'immediate' | 'daily' | 'off';
  channel?: 'immediate' | 'daily' | 'off';
  mentions?: 'immediate' | 'daily' | 'off';
  posts?: 'immediate' | 'daily' | 'off';
}) => async (dispatch: Dispatch) => {
  dispatch({ type: UPDATE_DIGEST_PREFERENCES_LOADING });
  try {
    const response = await updateDigestPreferencesApi(data);
    dispatch({
      type: UPDATE_DIGEST_PREFERENCES_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: UPDATE_DIGEST_PREFERENCES_ERROR,
      payload: error.response?.data?.message || 'Failed to update digest preferences',
    });
    throw error;
  }
};

// Channel Subscriptions

export const fetchChannelSubscriptions = (communityIdentifier?: string) => async (dispatch: Dispatch) => {
  dispatch({ type: GET_CHANNEL_SUBSCRIPTIONS_LOADING });
  try {
    const response = await getChannelSubscriptionsApi(communityIdentifier);
    dispatch({
      type: GET_CHANNEL_SUBSCRIPTIONS_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: GET_CHANNEL_SUBSCRIPTIONS_ERROR,
      payload: error.response?.data?.message || 'Failed to fetch channel subscriptions',
    });
    throw error;
  }
};

export const subscribeToChannel = (
  communityIdentifier: string,
  channelIdentifier: string,
  data: {
    frequency?: 'immediate' | 'daily' | 'off';
    emailEnabled?: boolean;
    pushEnabled?: boolean;
  }
) => async (dispatch: Dispatch) => {
  dispatch({ type: SUBSCRIBE_TO_CHANNEL_LOADING });
  try {
    const response = await subscribeToChannelApi(communityIdentifier, channelIdentifier, data);
    dispatch({
      type: SUBSCRIBE_TO_CHANNEL_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: SUBSCRIBE_TO_CHANNEL_ERROR,
      payload: error.response?.data?.message || 'Failed to subscribe to channel',
    });
    throw error;
  }
};

export const unsubscribeFromChannel = (
  communityIdentifier: string,
  channelIdentifier: string
) => async (dispatch: Dispatch) => {
  dispatch({ type: UNSUBSCRIBE_FROM_CHANNEL_LOADING });
  try {
    const response = await unsubscribeFromChannelApi(communityIdentifier, channelIdentifier);
    dispatch({
      type: UNSUBSCRIBE_FROM_CHANNEL_SUCCESS,
      payload: { communityIdentifier, channelIdentifier },
    });
    return response;
  } catch (error) {
    dispatch({
      type: UNSUBSCRIBE_FROM_CHANNEL_ERROR,
      payload: error.response?.data?.message || 'Failed to unsubscribe from channel',
    });
    throw error;
  }
};

export const muteChannel = (
  communityIdentifier: string,
  channelIdentifier: string,
  muteDurationHours: number = 24
) => async (dispatch: Dispatch) => {
  dispatch({ type: MUTE_CHANNEL_LOADING });
  try {
    const response = await muteChannelApi(communityIdentifier, channelIdentifier, muteDurationHours);
    dispatch({
      type: MUTE_CHANNEL_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: MUTE_CHANNEL_ERROR,
      payload: error.response?.data?.message || 'Failed to mute channel',
    });
    throw error;
  }
};

export const unmuteChannel = (
  communityIdentifier: string,
  channelIdentifier: string
) => async (dispatch: Dispatch) => {
  dispatch({ type: UNMUTE_CHANNEL_LOADING });
  try {
    const response = await unmuteChannelApi(communityIdentifier, channelIdentifier);
    dispatch({
      type: UNMUTE_CHANNEL_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: UNMUTE_CHANNEL_ERROR,
      payload: error.response?.data?.message || 'Failed to unmute channel',
    });
    throw error;
  }
};

// Post Subscriptions

export const fetchPostSubscriptions = (postId?: string) => async (dispatch: Dispatch) => {
  dispatch({ type: GET_POST_SUBSCRIPTIONS_LOADING });
  try {
    const response = await getPostSubscriptionsApi(postId);
    dispatch({
      type: GET_POST_SUBSCRIPTIONS_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: GET_POST_SUBSCRIPTIONS_ERROR,
      payload: error.response?.data?.message || 'Failed to fetch post subscriptions',
    });
    throw error;
  }
};

export const subscribeToPost = (
  postId: string,
  data: {
    frequency?: 'immediate' | 'daily' | 'off';
    emailEnabled?: boolean;
    pushEnabled?: boolean;
    digestInclusion?: 'include' | 'exclude';
  }
) => async (dispatch: Dispatch) => {
  dispatch({ type: SUBSCRIBE_TO_POST_LOADING });
  try {
    const response = await subscribeToPostApi(postId, data);
    dispatch({
      type: SUBSCRIBE_TO_POST_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: SUBSCRIBE_TO_POST_ERROR,
      payload: error.response?.data?.message || 'Failed to subscribe to post',
    });
    throw error;
  }
};

export const unsubscribeFromPost = (postId: string) => async (dispatch: Dispatch) => {
  dispatch({ type: UNSUBSCRIBE_FROM_POST_LOADING });
  try {
    const response = await unsubscribeFromPostApi(postId);
    dispatch({
      type: UNSUBSCRIBE_FROM_POST_SUCCESS,
      payload: { postId },
    });
    return response;
  } catch (error) {
    dispatch({
      type: UNSUBSCRIBE_FROM_POST_ERROR,
      payload: error.response?.data?.message || 'Failed to unsubscribe from post',
    });
    throw error;
  }
};

export const mutePost = (
  postId: string,
  muteDurationHours: number = 24
) => async (dispatch: Dispatch) => {
  dispatch({ type: MUTE_POST_LOADING });
  try {
    const response = await mutePostApi(postId, muteDurationHours);
    dispatch({
      type: MUTE_POST_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: MUTE_POST_ERROR,
      payload: error.response?.data?.message || 'Failed to mute post',
    });
    throw error;
  }
};

export const unmutePost = (postId: string) => async (dispatch: Dispatch) => {
  dispatch({ type: UNMUTE_POST_LOADING });
  try {
    const response = await unmutePostApi(postId);
    dispatch({
      type: UNMUTE_POST_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: UNMUTE_POST_ERROR,
      payload: error.response?.data?.message || 'Failed to unmute post',
    });
    throw error;
  }
};

// Admin Policies

export const fetchCommunityPolicy = (communityIdentifier: string) => async (dispatch: Dispatch) => {
  dispatch({ type: GET_COMMUNITY_POLICY_LOADING });
  try {
    const response = await getCommunityPolicyApi(communityIdentifier);
    dispatch({
      type: GET_COMMUNITY_POLICY_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: GET_COMMUNITY_POLICY_ERROR,
      payload: error.response?.data?.message || 'Failed to fetch community policy',
    });
    throw error;
  }
};

export const updateCommunityPolicy = (
  communityIdentifier: string,
  data: {
    policyMode?: 'recommended' | 'enforced';
    defaults?: {
      globalFrequency?: 'immediate' | 'daily' | 'off';
      emailEnabled?: boolean;
      pushEnabled?: boolean;
    };
    emergencyEnabled?: boolean;
    emergencyActors?: string[];
  }
) => async (dispatch: Dispatch) => {
  dispatch({ type: UPDATE_COMMUNITY_POLICY_LOADING });
  try {
    const response = await updateCommunityPolicyApi(communityIdentifier, data);
    dispatch({
      type: UPDATE_COMMUNITY_POLICY_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: UPDATE_COMMUNITY_POLICY_ERROR,
      payload: error.response?.data?.message || 'Failed to update community policy',
    });
    throw error;
  }
};

export const fetchChannelPolicy = (
  communityIdentifier: string,
  channelIdentifier: string
) => async (dispatch: Dispatch) => {
  dispatch({ type: GET_CHANNEL_POLICY_LOADING });
  try {
    const response = await getChannelPolicyApi(communityIdentifier, channelIdentifier);
    dispatch({
      type: GET_CHANNEL_POLICY_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: GET_CHANNEL_POLICY_ERROR,
      payload: error.response?.data?.message || 'Failed to fetch channel policy',
    });
    throw error;
  }
};

export const updateChannelPolicy = (
  communityIdentifier: string,
  channelIdentifier: string,
  data: {
    policyMode?: 'recommended' | 'enforced';
    defaults?: {
      frequency?: 'immediate' | 'daily' | 'off';
      emailEnabled?: boolean;
      pushEnabled?: boolean;
    };
  }
) => async (dispatch: Dispatch) => {
  dispatch({ type: UPDATE_CHANNEL_POLICY_LOADING });
  try {
    const response = await updateChannelPolicyApi(communityIdentifier, channelIdentifier, data);
    dispatch({
      type: UPDATE_CHANNEL_POLICY_SUCCESS,
      payload: response.data,
    });
    return response;
  } catch (error) {
    dispatch({
      type: UPDATE_CHANNEL_POLICY_ERROR,
      payload: error.response?.data?.message || 'Failed to update channel policy',
    });
    throw error;
  }
};
